import { Popover, Box, useTheme } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import { IconButton } from '@/components/DesignSystem';
import './DatePickerStyles.css';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { DatePickerCalendar } from './DatePickerCalendar';
import { DatePickerInput, toComparableTime } from './DatePickerInput';

const DATE_FORMAT = 'MM/dd/yyyy';
const DATE_FORMAT_LENGTH = 'MM/DD/YYYY'.length;

type TypedTextProblem = 'notADate' | 'outOfRange';

export interface DatePickerDropdownProps {
  disabled?: boolean,
  errorMessage?: string,
  hideHelperText?: boolean,
  label: string,
  // Inclusive bounds as yyyy-mm-dd; days outside them can't be picked.
  maxDate?: string,
  minDate?: string,
  onChange: (value?: Date) => void,
  // Called when the user empties the field, which onChange alone can't tell apart from invalid text.
  onClear?: () => void,
  // Reports false while the field holds typed text that is not a valid date within the bounds.
  onValidityChange?: (isValid: boolean) => void,
  // Replaces the generic error when a typed date is real but outside minDate/maxDate.
  outOfRangeErrorMessage?: string,
  // Holds a typed date or clear until the input loses focus or Enter is pressed, so a caller that
  // saves on every change doesn't save each date passed through while a date is typed over.
  sendsTypedDateOnBlur?: boolean,
  testID: string,
  value?: Date,
  width?: number,
}

export const DatePickerDropdown = ({
  disabled,
  errorMessage,
  hideHelperText = false,
  label,
  maxDate,
  minDate,
  onChange,
  onClear,
  onValidityChange,
  outOfRangeErrorMessage,
  sendsTypedDateOnBlur = false,
  testID,
  value,
  width,
}: DatePickerDropdownProps) => {
  const { t } = useTranslation();
  const inputContainerRef = useRef<HTMLDivElement>(null);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [typedText, setTypedText] = useState('');
  const [typedTextProblem, setTypedTextProblem] = useState<TypedTextProblem>();
  const pendingTypedDate = useRef<{ date?: Date }>();
  const theme = useTheme();

  useEffect(() => {
    if (!isFocused) {
      setTypedTextProblem(undefined);
    }
  }, [toComparableTime(value)]);

  useEffect(() => {
    onValidityChange?.(!typedTextProblem);
  }, [typedTextProblem]);

  const handleSelect = (date: Date) => {
    pendingTypedDate.current = undefined;
    setTypedTextProblem(undefined);
    onChange(date);
    setIsCalendarOpen(false);
  };

  const isWithinBounds = (date: Date) => {
    const dateOnly = DateTime.fromJSDate(date).toISODate() ?? '';
    return (!minDate || dateOnly >= minDate) && (!maxDate || dateOnly <= maxDate);
  };

  // An incomplete, invalid or out-of-bounds typed date clears the value.
  const handleTypedDate = (date?: Date) => {
    const boundedDate = date && isWithinBounds(date) ? date : undefined;
    if (sendsTypedDateOnBlur) {
      pendingTypedDate.current = { date: boundedDate };
    } else {
      onChange(boundedDate);
    }
  };

  const sendPendingTypedDate = () => {
    const pending = pendingTypedDate.current;
    pendingTypedDate.current = undefined;
    if (!pending) {
      return;
    }
    if (!typedText) {
      onClear?.();
    } else if (toComparableTime(pending.date) !== toComparableTime(value)) {
      onChange(pending.date);
    }
  };

  const problemWith = (text: string): TypedTextProblem | undefined => {
    const date = DateTime.fromFormat(text, DATE_FORMAT);
    if (!text) {
      return undefined;
    }
    if (!date.isValid) {
      return 'notADate';
    }
    return isWithinBounds(date.toJSDate()) ? undefined : 'outOfRange';
  };

  const handleTypedText = (text: string) => {
    setTypedText(text);
    setTypedTextProblem(problemWith(text));
    if (!text && !sendsTypedDateOnBlur) {
      onClear?.();
    }
  };

  const isTypingIncompleteDate = isFocused && typedText.length < DATE_FORMAT_LENGTH;
  const typedTextError = typedTextProblem === 'outOfRange' && outOfRangeErrorMessage
    ? outOfRangeErrorMessage
    : t($ => $.shared.inputValidation.invalidDate);
  const invalidDateError = typedTextProblem && !isTypingIncompleteDate
    ? typedTextError
    : undefined;
  const shownError = invalidDateError ?? errorMessage;
  const validValue = toComparableTime(value) === undefined ? undefined : value;

  return (
    <>
      <Box
        onBlur={(event) => {
          if (event.target.tagName === 'INPUT') {
            setIsFocused(false);
            sendPendingTypedDate();
          }
        }}
        onFocus={(event) => event.target.tagName === 'INPUT' && setIsFocused(true)}
        onKeyDown={(event) => event.key === 'Enter' && sendPendingTypedDate()}
        ref={inputContainerRef}
        sx={{ display: 'flex', flex: width ? '0 0 auto' : 1 }}
      >
        <DatePickerInput
          containerSx={{ borderBottom: '1px solid #D8D8D8' }}
          disabled={disabled}
          error={!!shownError}
          helperText={shownError ?? (!hideHelperText && 'MM/DD/YYYY')}
          label={label}
          onChangeDate={handleTypedDate}
          onChangeText={handleTypedText}
          sendsDateWhileTyping
          testID={`${testID}-input`}
          trailingIcon={disabled ? undefined : () => (
            <Box mr={0.8}>
              <IconButton
                onClick={() => setIsCalendarOpen(true)}
                testID={`${testID}-calendar-button`}
              >
                <CalendarTodayIcon sx={{ color: theme.palette.text.secondary }}/>
              </IconButton>
            </Box>
          )}
          value={validValue}
          variant="filled"
          width={width}
        />
      </Box>
      <Popover
        PaperProps={{
          sx: {
            borderRadius: "16px",
            width: "360px",
          },
        }}
        anchorEl={inputContainerRef.current}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        onClose={() => setIsCalendarOpen(false)}
        open={isCalendarOpen}
        sx={{
          marginTop: "7px",
        }}
      >
        <DatePickerCalendar
          internalStartDate={validValue}
          maxDate={maxDate}
          minDate={minDate}
          onChange={handleSelect}
          testID={testID}
          variant="dropdown"
        />
      </Popover>
    </>
  );
};
